import React from 'react';

const makeProgressText = (base) => {
  if (!base || typeof base !== 'string') return 'Updating';
  const trimmed = base.trim();
  if (trimmed.length === 0) return 'Updating';

  const isCapitalized = /^[A-Z]/.test(trimmed);
  const lower = trimmed.toLowerCase();

  let prog;
  if (lower.endsWith('ie')) {
    prog = trimmed.slice(0, -2) + 'ying';
  } else if (lower.endsWith('e')) {
    prog = trimmed.slice(0, -1) + 'ing';
  } else if (lower.endsWith('y')) {
    prog = trimmed.slice(0, -1) + 'ying';
  } else {
    prog = trimmed + 'ing';
  }

  if (isCapitalized) {
    prog = prog.charAt(0).toUpperCase() + prog.slice(1);
  }
  return prog;
};

const EditModal = ({
  show,
  title = "Edit",
  onCancel,
  onSubmit,
  loading = false,
  children,
  submitText = "Save",
  submittingText = null,
  size = "md" // sm, md, lg, xl
}) => {
  if (!show) return null;

  const finalSubmitText = submitText || "Save";
  const finalSubmittingText = submittingText || `${makeProgressText(finalSubmitText)}...`;

  const handleSubmit = (e) => {
    if (typeof onSubmit === 'function') {
      e.preventDefault();
      onSubmit(e);
    } else {
      e.preventDefault();
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'max-w-sm w-full';
      case 'lg':
        return 'max-w-2xl w-full';
      case 'xl':
        return 'max-w-4xl w-full';
      default:
        return 'max-w-md w-full';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className={`
          bg-white rounded-xl shadow-xl border border-gray-200
          ${getSizeClasses()}
          max-h-[90vh] overflow-hidden
          mx-4 sm:mx-6
          transform transition-all duration-200 ease-out
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/50">
          <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[calc(90vh-140px)]">
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1 custom-scrollbar">
            {children}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50/30">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-btn-primary focus:ring-offset-1 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-btn-primary border border-transparent rounded-lg hover:bg-btn-primary-hover focus:ring-2 focus:ring-btn-primary focus:ring-offset-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  {finalSubmittingText}
                </span>
              ) : (
                finalSubmitText
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditModal;